/**
 * CaseDetailPage — the stage-aware primary surface (wireframe 2).
 *
 * Covers PAC-010/017/018/021, ENR-024, PAC-023, FND-006. The WorkflowTracker
 * stages (Intake · Screening · Employer · Application · Enrolled · Retention)
 * are selectable: the PRIMARY panel renders the selected stage's structured
 * task, while stages past the case's current stage are dependency-gated
 * ("complete prior steps"). The RIGHT context panel carries the patient
 * summary, the case-history timeline, related flags, and the advisory
 * "Next best action" AI insight card.
 *
 * Per stage:
 *   - Intake      — attempt logging (POST /cases/{id}/attempts) + overview.
 *   - Screening   — auto-populated screening form (confirm/correct, changed-
 *                   field highlight) + family/household discovery checklist.
 *   - Employer    — attached employer, benefits-recency badge, recommended
 *                   effective date card + override (date + reason).
 *   - Application / Enrolled / Retention — light titled panels (later phases
 *                   deepen these).
 *
 * The new stage hooks (useScreening / useHousehold / useEffectiveDate /
 * useBenefitsRecency) degrade gracefully: an unimplemented endpoint (404)
 * renders a friendly disabled state rather than crashing the page.
 */
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCase } from '../hooks/useCase';
import { useScreening, type ScreeningField } from '../hooks/useScreening';
import { useHousehold, type AddMemberBody } from '../hooks/useHousehold';
import {
  useEffectiveDate,
  useBenefitsRecency,
} from '../hooks/useEffectiveDate';
import { useEmployerOutreach } from '../hooks/useEmployerOutreach';
import {
  useStateRouting,
  usePlanEligibility,
  usePacket,
} from '../hooks/useApplicationStage';
import { useEnrollment } from '../hooks/useEnrollment';
import { useClinical, type ClinicalDecisionBody } from '../hooks/useClinical';
import {
  usePropensity,
  useEligibilityRealtime,
  useNextBestAction,
  type PropensityScore,
  type EligibilityRealtime,
} from '../hooks/useIntelligence';
import type { Case, CaseEvent } from '../api/types';
import { colors, tints } from '../theme';
import { Card } from '../components/Card';
import { ThreePanel } from '../components/ThreePanel';
import { StatusBadge } from '../components/StatusBadge';
import { PriorityPill, tierForScore } from '../components/PriorityPill';
import { AIInsightCard } from '../components/AIInsightCard';
import { WorkflowTracker, type WorkflowStage, type StageState } from '../components/WorkflowTracker';
import { LoadingIndicator } from '../components/LoadingIndicator';
import { EmptyState } from '../components/EmptyState';
import { useShellSlot } from '../components/shellContext';

const CONTACT_METHODS = [
  { value: 'in_person', label: 'In person' },
  { value: 'hospital_phone', label: 'Hospital phone' },
  { value: 'ringcentral', label: 'RingCentral' },
];
const OUTCOME_CODES = [
  { value: 'reached_completed', label: 'Reached — completed' },
  { value: 'no_answer_left_vm', label: 'No answer — left VM' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'callback_scheduled', label: 'Callback scheduled' },
  { value: 'declined', label: 'Declined' },
];
const CONSENT_TYPES = [
  { value: 'tcpa', label: 'TCPA' },
  { value: 'hipaa', label: 'HIPAA' },
  { value: 'e-sign', label: 'E-sign' },
];
const CONSENT_CHANNELS = [
  { value: 'in_app', label: 'In app' },
  { value: 'iPad', label: 'iPad' },
  { value: 'SMS link', label: 'SMS link' },
  { value: 'verbal', label: 'Verbal' },
];
const DEFAULT_CONSENT_VERSION = '2026-01';

const RELATIONSHIPS = [
  { value: 'spouse', label: 'Spouse / partner' },
  { value: 'child', label: 'Child' },
  { value: 'parent', label: 'Parent' },
  { value: 'sibling', label: 'Sibling' },
  { value: 'other', label: 'Other' },
];

/** Canonical enrollment lifecycle stages for the selectable tracker. */
const LIFECYCLE: { key: string; label: string }[] = [
  { key: 'intake', label: 'Intake' },
  { key: 'screening', label: 'Screening' },
  { key: 'employer_research', label: 'Employer' },
  { key: 'application', label: 'Application' },
  { key: 'enrolled', label: 'Enrolled' },
  { key: 'retention', label: 'Retention' },
];

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString();
}

/** Index of the case's current stage within the lifecycle. */
function currentStageIndex(currentStage: string | undefined): number {
  const norm = (currentStage ?? '').toLowerCase();
  const idx = LIFECYCLE.findIndex((s) => norm.includes(s.key) || s.key.includes(norm));
  return idx >= 0 ? idx : 0;
}

/** Build tracker stages relative to the case's current stage. */
function buildStages(currentIdx: number): WorkflowStage[] {
  return LIFECYCLE.map((s, i) => {
    let state: StageState;
    if (i < currentIdx) state = 'complete';
    else if (i === currentIdx) state = 'current';
    else if (i === currentIdx + 1) state = 'upcoming';
    else state = 'locked';
    return { key: s.key, label: s.label, state };
  });
}

function eventLabel(eventType: string): string {
  return eventType.replace(/[._]/g, ' ');
}

function CaseHistory({ events }: { events: CaseEvent[] }) {
  if (events.length === 0) {
    return <EmptyState title="No history yet" description="Activity will appear here as the case progresses." />;
  }
  return (
    <ol style={timeline}>
      {events.map((evt) => (
        <li key={evt.id} style={timelineItem}>
          <span style={timelineDot} aria-hidden />
          <div style={{ flex: 1 }}>
            <div style={timelineHead}>
              <span style={eventTypeStyle}>{eventLabel(evt.event_type)}</span>
              <span style={eventTime}>{fmtDateTime(evt.created_at)}</span>
            </div>
            {evt.payload && Object.keys(evt.payload).length > 0 && (
              <div style={payloadWrap}>
                {Object.entries(evt.payload).map(([k, v]) => (
                  <span key={k} style={chip}>
                    {k.replace(/_/g, ' ')}: {String(v)}
                  </span>
                ))}
              </div>
            )}
            {evt.actor_id && <div style={eventActor}>by {evt.actor_id}</div>}
          </div>
        </li>
      ))}
    </ol>
  );
}

function PatientSummary({ caseRecord }: { caseRecord: Case }) {
  const rows: { label: string; value: string }[] = [
    { label: 'Patient', value: caseRecord.patient_id },
    { label: 'Employer', value: caseRecord.employer_id ?? '—' },
    { label: 'Assignee', value: caseRecord.assigned_user_id ?? 'Unassigned' },
    { label: 'Eff. date', value: fmtDate(caseRecord.recommended_effective_date) },
    { label: 'Created', value: fmtDateTime(caseRecord.created_at) },
    { label: 'Updated', value: fmtDateTime(caseRecord.updated_at) },
  ];
  return (
    <div>
      <div style={sectionTitle}>Patient summary</div>
      <dl style={summaryGrid}>
        {rows.map((r) => (
          <div key={r.label} style={summaryRow}>
            <dt style={summaryLabel}>{r.label}</dt>
            <dd style={summaryValue}>{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** A small related-flags panel derived from case state (right context). */
function RelatedFlags({ caseRecord, breachRisk }: { caseRecord: Case; breachRisk: boolean }) {
  const flags: { label: string; tier: 'danger' | 'warning' | 'info' | 'neutral' }[] = [];
  if (breachRisk) flags.push({ label: 'SLA breach risk', tier: 'danger' });
  if (!caseRecord.employer_id) flags.push({ label: 'No employer matched', tier: 'warning' });
  if (!caseRecord.assigned_user_id) flags.push({ label: 'Unassigned', tier: 'warning' });
  if (!caseRecord.recommended_effective_date) flags.push({ label: 'No effective date', tier: 'info' });
  return (
    <div>
      <div style={sectionTitle}>Related flags</div>
      {flags.length === 0 ? (
        <div style={mutedNote}>No active flags.</div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {flags.map((f) => (
            <StatusBadge key={f.label} status={f.tier} label={f.label} />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Right-context: Intelligence surfaces (propensity + real-time eligibility)
// ---------------------------------------------------------------------------

function propensityBandTier(band: PropensityScore['band']): 'success' | 'warning' | 'neutral' {
  if (band === 'high') return 'success';
  if (band === 'med') return 'warning';
  return 'neutral';
}

/** Advisory propensity-to-enroll card (AI-001), rendered as an AI insight. */
function PropensityCard({ caseId }: { caseId: string }) {
  const { propensity, available } = usePropensity(caseId);
  if (!propensity) return null;
  const p = propensity;
  const maxWeight = Math.max(...p.factors.map((f) => f.weight), 0.01);

  return (
    <AIInsightCard confidence={p.score} heading="Propensity to enroll" readOnly>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={propensityScore}>{Math.round(p.score * 100)}</span>
          <StatusBadge status={propensityBandTier(p.band)} label={`${p.band} band`} />
        </div>

        <div style={modelVsRules}>
          <span>Model {p.model_version}</span>
          <span style={{ color: colors.textMuted }}>vs</span>
          <span>Champion {p.champion}</span>
        </div>

        {p.explanation && <div style={{ fontSize: '0.82rem', color: colors.textSecondary, lineHeight: 1.45 }}>{p.explanation}</div>}

        <div>
          <div style={{ ...sectionTitle, marginBottom: 6 }}>Top factors</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            {p.factors.slice(0, 5).map((f) => (
              <div key={f.label} style={factorBarRow}>
                <span style={factorBarLabel}>{f.label}</span>
                <span style={factorBarTrack}>
                  <span style={{ ...factorBarFill, width: `${(f.weight / maxWeight) * 100}%` }} />
                </span>
              </div>
            ))}
          </div>
        </div>

        {!available && <div style={{ fontSize: '0.72rem', color: colors.textMuted }}>Representative score — pending live model.</div>}
      </div>
    </AIInsightCard>
  );
}

/**
 * Next-best-action card (advisory / HITL). Replaces the previously hardcoded
 * recommendation with a live, stage-aware suggestion from the AI Gateway
 * (GET /ai/next-best-action). Keeps Accept/Dismiss — the advocate confirms.
 */
function NextBestActionCard({ caseId }: { caseId: string }) {
  const { action, available } = useNextBestAction(caseId);
  if (!action) return null;
  return (
    <AIInsightCard confidence={action.confidence} heading="Next best action" acceptLabel="Do this">
      <div>{action.action}</div>
      {action.rationale && (
        <div style={{ fontSize: '0.82rem', color: colors.textSecondary, marginTop: 6, lineHeight: 1.45 }}>
          {action.rationale}
        </div>
      )}
      {!available && (
        <div style={{ fontSize: '0.72rem', color: colors.textMuted, marginTop: 6 }}>
          Representative — pending live model.
        </div>
      )}
    </AIInsightCard>
  );
}

function eligTier(status: EligibilityRealtime['status']): 'success' | 'danger' | 'warning' | 'neutral' {
  if (status === 'eligible') return 'success';
  if (status === 'ineligible') return 'danger';
  if (status === 'pending') return 'warning';
  return 'neutral';
}

/** Real-time eligibility indicator (PAC-019) in the right-context panel. */
function EligibilityIndicator({ caseId }: { caseId: string }) {
  const { eligibility } = useEligibilityRealtime(caseId);
  if (!eligibility) return null;
  const e = eligibility;
  return (
    <div>
      <div style={sectionTitle}>Real-time eligibility</div>
      <div style={eligCardRt}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <StatusBadge status={eligTier(e.status)} label={e.status} />
          <span style={memberMeta}>{e.source}</span>
          <span style={automatedBadge} title="Run automatically by the platform — no manual entry">
            ✦ Automated
          </span>
        </div>
        {e.plan && <div style={{ fontSize: '0.86rem', fontWeight: 600, marginTop: 6 }}>{e.plan}</div>}
        {e.detail && <div style={{ ...memberMeta, marginTop: 2 }}>{e.detail}</div>}
        <div style={{ ...memberMeta, marginTop: 6 }}>Checked automatically · {fmtDateTime(e.checked_at)}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stage: Intake
// ---------------------------------------------------------------------------

function IntakeStage({
  caseRecord,
  isWorking,
  onLogAttempt,
}: {
  caseRecord: Case;
  isWorking: boolean;
  onLogAttempt: (method: string, outcome: string, note: string) => Promise<boolean>;
}) {
  const [contactMethod, setContactMethod] = useState(CONTACT_METHODS[0].value);
  const [outcomeCode, setOutcomeCode] = useState(OUTCOME_CODES[0].value);
  const [notes, setNotes] = useState('');

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await onLogAttempt(contactMethod, outcomeCode, notes.trim());
    if (ok) setNotes('');
  };

  const overview: { label: string; value: string }[] = [
    { label: 'Stage', value: caseRecord.stage.replace(/_/g, ' ') },
    { label: 'Status', value: caseRecord.status.replace(/_/g, ' ') },
    { label: 'Patient', value: caseRecord.patient_id },
    { label: 'Created', value: fmtDateTime(caseRecord.created_at) },
  ];

  return (
    <>
      <Card title="Case overview">
        <dl style={overviewGrid}>
          {overview.map((o) => (
            <div key={o.label} style={overviewItem}>
              <dt style={overviewLabel}>{o.label}</dt>
              <dd style={overviewValue}>{o.value}</dd>
            </div>
          ))}
        </dl>
      </Card>

      <Card title="Log attempt">
        <form onSubmit={handle} style={form}>
          <div style={fieldRow}>
            <div style={field}>
              <label style={labelStyle} htmlFor="contact-method">
                Contact method
              </label>
              <select
                id="contact-method"
                value={contactMethod}
                onChange={(e) => setContactMethod(e.target.value)}
                style={input}
              >
                {CONTACT_METHODS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={field}>
              <label style={labelStyle} htmlFor="outcome-code">
                Outcome
              </label>
              <select
                id="outcome-code"
                value={outcomeCode}
                onChange={(e) => setOutcomeCode(e.target.value)}
                style={input}
              >
                {OUTCOME_CODES.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div style={field}>
            <label style={labelStyle} htmlFor="attempt-notes">
              Notes
            </label>
            <textarea
              id="attempt-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="input"
              style={{ resize: 'vertical' }}
              placeholder="Optional structured note…"
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={isWorking}>
            Log attempt
          </button>
        </form>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Stage: Screening (auto-pop form + household discovery)
// ---------------------------------------------------------------------------

function ScreeningStage({ caseId }: { caseId: string }) {
  const screening = useScreening(caseId);
  const household = useHousehold(caseId);

  return (
    <>
      <ScreeningForm screening={screening} />
      <HouseholdChecklist household={household} />
    </>
  );
}

function ScreeningForm({
  screening,
}: {
  screening: ReturnType<typeof useScreening>;
}) {
  const { fields, available, isLoading, isWorking, actionError, save } = screening;
  // Local working copy so we can highlight changed fields before save.
  const [draft, setDraft] = useState<ScreeningField[]>([]);
  useEffect(() => {
    setDraft(fields);
  }, [fields]);

  const dirty = useMemo(
    () =>
      draft.some((f, i) => {
        const orig = fields[i];
        return !orig || f.value !== orig.value || f.confirmed !== orig.confirmed;
      }),
    [draft, fields]
  );

  const setValue = (idx: number, value: string) =>
    setDraft((d) => d.map((f, i) => (i === idx ? { ...f, value } : f)));
  const setConfirmed = (idx: number, confirmed: boolean) =>
    setDraft((d) => d.map((f, i) => (i === idx ? { ...f, confirmed } : f)));
  const confirmAll = () => setDraft((d) => d.map((f) => ({ ...f, confirmed: true })));

  let body: React.ReactNode;
  if (isLoading) {
    body = <LoadingIndicator text="Loading screening…" />;
  } else if (!available) {
    body = (
      <EmptyState
        title="Screening template not available"
        description="Auto-population from prior data isn't connected for this case yet. Confirm details on the next call."
      />
    );
  } else if (draft.length === 0) {
    body = <EmptyState title="No screening fields" description="No pre-populated fields were found." />;
  } else {
    const autoFilled = draft.filter(
      (f) => f.source && f.source !== 'manual' && (f.value ?? '') !== ''
    );
    const bySource = autoFilled.reduce<Record<string, number>>((acc, f) => {
      const s = f.source.replace(/_/g, ' ');
      acc[s] = (acc[s] ?? 0) + 1;
      return acc;
    }, {});
    body = (
      <div style={form}>
        {autoFilled.length > 0 && (
          <div style={autoFillBanner}>
            <span style={autoFillSpark} aria-hidden>
              ✦
            </span>
            <div>
              <strong>
                {autoFilled.length} of {draft.length} fields auto-filled from census
              </strong>{' '}
              — confirm or correct. Re-keying avoided.
              {Object.keys(bySource).length > 0 && (
                <div style={autoFillSources}>
                  {Object.entries(bySource).map(([src, count]) => (
                    <span key={src} style={sourceTag}>
                      {count} from {src}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        <div style={mutedNote}>
          Fields pre-populated from prior data. Changed values are highlighted — confirm or correct each.
        </div>
        {draft.map((f, i) => {
          const orig = fields[i];
          const changed = !!orig && f.value !== orig.value;
          return (
            <div key={f.key} style={screeningRow}>
              <div style={field}>
                <label style={labelStyle} htmlFor={`scr-${f.key}`}>
                  {f.label}
                  <span style={sourceTag}>{f.source.replace(/_/g, ' ')}</span>
                  {changed && <span style={changedTag}>changed</span>}
                </label>
                <input
                  id={`scr-${f.key}`}
                  type="text"
                  value={f.value ?? ''}
                  onChange={(e) => setValue(i, e.target.value)}
                  className="input"
                  style={changed ? changedInput : undefined}
                />
              </div>
              <label style={confirmCheck}>
                <input
                  type="checkbox"
                  checked={f.confirmed}
                  onChange={(e) => setConfirmed(i, e.target.checked)}
                />
                Confirmed
              </label>
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            className="btn btn-primary"
            disabled={isWorking || !dirty}
            onClick={() => save(draft)}
          >
            Save screening
          </button>
          <button type="button" className="btn btn-secondary" onClick={confirmAll}>
            Confirm all
          </button>
        </div>
        {actionError && (
          <p style={errorText} role="alert">
            {actionError.message}
          </p>
        )}
      </div>
    );
  }

  return <Card title="Screening (auto-populated)">{body}</Card>;
}

function HouseholdChecklist({
  household,
}: {
  household: ReturnType<typeof useHousehold>;
}) {
  const { members, available, isLoading, isWorking, actionError, addMember } = household;
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState(RELATIONSHIPS[0].value);
  const [age, setAge] = useState('');
  const [eligible, setEligible] = useState(false);
  const [candidate, setCandidate] = useState(false);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const body: AddMemberBody = {
      name: name.trim(),
      relationship,
      age: age.trim() ? Number(age) : null,
      eligible,
      creates_case_candidate: candidate,
    };
    const created = await addMember(body);
    if (created) {
      setName('');
      setAge('');
      setRelationship(RELATIONSHIPS[0].value);
      setEligible(false);
      setCandidate(false);
      setShowAdd(false);
    }
  };

  let body: React.ReactNode;
  if (isLoading) {
    body = <LoadingIndicator text="Loading household…" />;
  } else if (!available) {
    body = (
      <EmptyState
        title="Household discovery not available"
        description="The family/household roster isn't connected for this case yet."
      />
    );
  } else {
    body = (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {members.length === 0 ? (
          <EmptyState
            title="No household members yet"
            description="Ask the patient about spouse, children, and dependents — eligible members spawn follow-on cases."
          />
        ) : (
          <ul style={memberList}>
            {members.map((m) => (
              <li key={m.id} style={memberItem}>
                <div style={{ flex: 1 }}>
                  <div style={memberName}>
                    {m.name}
                    <span style={relTag}>{m.relationship.replace(/_/g, ' ')}</span>
                    {m.age != null && <span style={memberMeta}>age {m.age}</span>}
                  </div>
                  {m.notes && <div style={memberMeta}>{m.notes}</div>}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  {m.eligible && <StatusBadge status="success" label="Eligible" />}
                  {m.creates_case_candidate && (
                    <StatusBadge status="info" label="Creates follow-on case" />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {showAdd ? (
          <form onSubmit={handleAdd} style={form}>
            <div style={fieldRow}>
              <div style={field}>
                <label style={labelStyle} htmlFor="hh-name">
                  Name
                </label>
                <input
                  id="hh-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input"
                  placeholder="Member name"
                />
              </div>
              <div style={field}>
                <label style={labelStyle} htmlFor="hh-rel">
                  Relationship
                </label>
                <select
                  id="hh-rel"
                  value={relationship}
                  onChange={(e) => setRelationship(e.target.value)}
                  style={input}
                >
                  {RELATIONSHIPS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ ...field, maxWidth: 90 }}>
                <label style={labelStyle} htmlFor="hh-age">
                  Age
                </label>
                <input
                  id="hh-age"
                  type="number"
                  min={0}
                  value={age}
                  onChange={(e) => setAge(e.target.value)}
                  className="input"
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <label style={confirmCheck}>
                <input type="checkbox" checked={eligible} onChange={(e) => setEligible(e.target.checked)} />
                Likely eligible
              </label>
              <label style={confirmCheck}>
                <input
                  type="checkbox"
                  checked={candidate}
                  onChange={(e) => setCandidate(e.target.checked)}
                />
                Creates follow-on case
              </label>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="submit" className="btn btn-primary" disabled={isWorking || !name.trim()}>
                Add member
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowAdd(false)}>
                Cancel
              </button>
            </div>
            {actionError && (
              <p style={errorText} role="alert">
                {actionError.message}
              </p>
            )}
          </form>
        ) : (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowAdd(true)}>
            + Add member
          </button>
        )}
      </div>
    );
  }

  return <Card title="Family / household discovery">{body}</Card>;
}

// ---------------------------------------------------------------------------
// Stage: Employer (benefits recency + recommended effective date)
// ---------------------------------------------------------------------------

function recencyTier(status: string): 'success' | 'warning' | 'neutral' {
  const s = status.toLowerCase();
  if (s === 'current' || s === 'valid' || s === 'recent') return 'success';
  if (s === 'stale' || s === 'expiring' || s === 'outdated') return 'warning';
  return 'neutral';
}

const PHONE_OUTCOMES = [
  { value: 'reached_completed', label: 'Reached — completed' },
  { value: 'left_voicemail', label: 'Left voicemail' },
  { value: 'no_answer', label: 'No answer' },
  { value: 'callback_scheduled', label: 'Callback scheduled' },
  { value: 'wrong_number', label: 'Wrong number' },
];
const RESPONSE_CHANNELS = [
  { value: 'email', label: 'Email' },
  { value: 'fax', label: 'Fax' },
  { value: 'phone', label: 'Phone' },
  { value: 'portal', label: 'Portal' },
];

function emailStepTier(status: string): 'success' | 'warning' | 'info' | 'neutral' {
  const s = status.toLowerCase();
  if (s === 'responded' || s === 'sent') return 'success';
  if (s === 'pending') return 'warning';
  if (s === 'skipped') return 'neutral';
  return 'info';
}

/**
 * Employer-outreach panel (ENR-025/026/027) + in-flow eligibility check
 * (INT-005). Phone attempt log, email cadence + advance, response intake, and a
 * "Run eligibility check" action whose result rides in the integration job.
 */
function EmployerOutreachPanel({ caseId, caseRecord }: { caseId: string; caseRecord: Case }) {
  const outreach = useEmployerOutreach(caseId);
  const [phoneOutcome, setPhoneOutcome] = useState(PHONE_OUTCOMES[0].value);
  const [phoneNotes, setPhoneNotes] = useState('');
  const [showResponse, setShowResponse] = useState(false);
  const [respChannel, setRespChannel] = useState(RESPONSE_CHANNELS[0].value);
  const [respSummary, setRespSummary] = useState('');
  const [respAttach, setRespAttach] = useState('');

  const handlePhone = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await outreach.logPhoneAttempt({ outcome: phoneOutcome, notes: phoneNotes.trim() || undefined });
    if (ok) setPhoneNotes('');
  };

  const handleResponse = async (e: React.FormEvent) => {
    e.preventDefault();
    const ok = await outreach.intakeResponse({
      channel: respChannel,
      summary: respSummary.trim() || undefined,
      attachment_ref: respAttach.trim() || undefined,
    });
    if (ok) {
      setShowResponse(false);
      setRespSummary('');
      setRespAttach('');
    }
  };

  const handleEligibility = () => {
    // The eligibility connector keys on a member reference; the patient id is
    // the member ref for this demo (PHI never travels inline — INT-005).
    void outreach.runEligibility(caseRecord.patient_id, caseRecord.recommended_effective_date ?? undefined);
  };

  if (outreach.isLoading) {
    return (
      <Card title="Employer outreach">
        <LoadingIndicator text="Loading outreach…" />
      </Card>
    );
  }

  if (!outreach.available) {
    return (
      <Card title="Employer outreach">
        <EmptyState
          title="Outreach not available"
          description="The employer-outreach workflow isn't connected for this case yet. Phone, email cadence, and response intake will appear here."
        />
      </Card>
    );
  }

  const o = outreach.outreach;
  const elig = outreach.eligibilityJob;

  return (
    <Card
      title="Employer outreach"
      actions={
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={handleEligibility}
          disabled={outreach.eligibilityWorking}
        >
          {outreach.eligibilityWorking ? 'Checking…' : 'Run eligibility check'}
        </button>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {outreach.eligibilityError && (
          <p style={errorText} role="alert">
            Eligibility check failed: {outreach.eligibilityError.message}
          </p>
        )}
        {elig && (
          <div style={eligResult}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <StatusBadge value={elig.status} />
              <span style={mutedNote}>
                OneSource · {elig.connector} · {fmtDateTime(elig.updated_at)}
              </span>
            </div>
            {elig.result && Object.keys(elig.result).length > 0 && (
              <div style={payloadWrap}>
                {Object.entries(elig.result).map(([k, v]) => (
                  <span key={k} style={chip}>
                    {k.replace(/_/g, ' ')}: {String(v)}
                  </span>
                ))}
              </div>
            )}
            {elig.error && <div style={errorText}>{elig.error}</div>}
          </div>
        )}

        {/* Phone attempt log */}
        <div>
          <div style={sectionTitle}>Phone attempts</div>
          {o && o.phone_attempts.length > 0 ? (
            <ul style={memberList}>
              {o.phone_attempts.map((a) => (
                <li key={a.id} style={outreachRow}>
                  <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>
                    {a.outcome.replace(/_/g, ' ')}
                  </span>
                  {a.notes && <span style={memberMeta}>{a.notes}</span>}
                  <span style={{ ...memberMeta, marginLeft: 'auto' }}>{fmtDateTime(a.created_at)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div style={mutedNote}>No phone attempts logged yet.</div>
          )}
          <form onSubmit={handlePhone} style={{ ...form, marginTop: 10 }}>
            <div style={fieldRow}>
              <div style={field}>
                <label style={labelStyle} htmlFor="emp-phone-outcome">
                  Outcome
                </label>
                <select
                  id="emp-phone-outcome"
                  value={phoneOutcome}
                  onChange={(e) => setPhoneOutcome(e.target.value)}
                  style={input}
                >
                  {PHONE_OUTCOMES.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ ...field, flex: 2 }}>
                <label style={labelStyle} htmlFor="emp-phone-notes">
                  Notes
                </label>
                <input
                  id="emp-phone-notes"
                  type="text"
                  value={phoneNotes}
                  onChange={(e) => setPhoneNotes(e.target.value)}
                  className="input"
                  placeholder="Optional note…"
                />
              </div>
            </div>
            <button type="submit" className="btn btn-secondary btn-sm" disabled={outreach.isWorking}>
              Log phone attempt
            </button>
          </form>
        </div>

        {/* Email cadence */}
        <div>
          <div style={sectionTitle}>Email cadence</div>
          {o && o.email_cadence.length > 0 ? (
            <ol style={cadenceList}>
              {o.email_cadence.map((s) => (
                <li key={s.step} style={cadenceItem}>
                  <span style={cadenceStepNum}>{s.step}</span>
                  <span style={{ flex: 1 }}>{s.label}</span>
                  <StatusBadge status={emailStepTier(s.status)} label={s.status} />
                  <span style={memberMeta}>{s.sent_at ? fmtDate(s.sent_at) : '—'}</span>
                </li>
              ))}
            </ol>
          ) : (
            <div style={mutedNote}>No email cadence configured yet.</div>
          )}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            style={{ marginTop: 10 }}
            disabled={outreach.isWorking || (o ? o.next_email_step == null : true)}
            onClick={() => outreach.advanceEmail({})}
          >
            {o && o.next_email_step != null ? `Send next email (step ${o.next_email_step})` : 'Cadence complete'}
          </button>
        </div>

        {/* Response intake */}
        <div>
          <div style={sectionTitle}>Responses</div>
          {o && o.responses.length > 0 ? (
            <ul style={memberList}>
              {o.responses.map((r) => (
                <li key={r.id} style={outreachRow}>
                  <StatusBadge status="info" label={r.channel} />
                  {r.summary && <span style={{ flex: 1 }}>{r.summary}</span>}
                  {r.attachment_ref && <StatusBadge status="success" label="Attached" />}
                  <span style={{ ...memberMeta, marginLeft: 'auto' }}>{fmtDateTime(r.created_at)}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div style={mutedNote}>No employer responses recorded yet.</div>
          )}
          {showResponse ? (
            <form onSubmit={handleResponse} style={{ ...form, marginTop: 10 }}>
              <div style={fieldRow}>
                <div style={field}>
                  <label style={labelStyle} htmlFor="emp-resp-channel">
                    Channel
                  </label>
                  <select
                    id="emp-resp-channel"
                    value={respChannel}
                    onChange={(e) => setRespChannel(e.target.value)}
                    style={input}
                  >
                    {RESPONSE_CHANNELS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={{ ...field, flex: 2 }}>
                  <label style={labelStyle} htmlFor="emp-resp-summary">
                    Summary
                  </label>
                  <input
                    id="emp-resp-summary"
                    type="text"
                    value={respSummary}
                    onChange={(e) => setRespSummary(e.target.value)}
                    className="input"
                    placeholder="What did the employer say?"
                  />
                </div>
              </div>
              <div style={field}>
                <label style={labelStyle} htmlFor="emp-resp-attach">
                  Attachment reference
                </label>
                <input
                  id="emp-resp-attach"
                  type="text"
                  value={respAttach}
                  onChange={(e) => setRespAttach(e.target.value)}
                  className="input"
                  placeholder="Document id / reference (optional)"
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="submit" className="btn btn-primary btn-sm" disabled={outreach.isWorking}>
                  Save response
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowResponse(false)}>
                  Cancel
                </button>
              </div>
            </form>
          ) : (
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              style={{ marginTop: 10 }}
              onClick={() => setShowResponse(true)}
            >
              + Record response
            </button>
          )}
        </div>

        {outreach.actionError && (
          <p style={errorText} role="alert">
            {outreach.actionError.message}
          </p>
        )}
      </div>
    </Card>
  );
}

function EmployerStage({ caseId, caseRecord }: { caseId: string; caseRecord: Case }) {
  const { recency, available: recencyAvail, isLoading: recencyLoading } = useBenefitsRecency(caseId);
  const ed = useEffectiveDate(caseId);
  const [showOverride, setShowOverride] = useState(false);
  const [overrideDate, setOverrideDate] = useState('');
  const [overrideReason, setOverrideReason] = useState('');

  const handleOverride = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!overrideDate || !overrideReason.trim()) return;
    const ok = await ed.override({ date: overrideDate, reason: overrideReason.trim() });
    if (ok) {
      setShowOverride(false);
      setOverrideDate('');
      setOverrideReason('');
    }
  };

  return (
    <>
      <EmployerOutreachPanel caseId={caseId} caseRecord={caseRecord} />

      <Card title="Attached employer">
        {caseRecord.employer_id ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div>
              <div style={overviewValue}>{caseRecord.employer_id}</div>
              <div style={mutedNote}>
                {recencyLoading ? (
                  'Checking benefits recency…'
                ) : recencyAvail && recency ? (
                  <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <StatusBadge status={recencyTier(recency.status)} label={`Benefits ${recency.status}`} />
                    <span>validated {fmtDate(recency.validated_at)}</span>
                  </span>
                ) : (
                  'Benefits recency not validated.'
                )}
              </div>
            </div>
            <Link to="/employers" className="btn btn-secondary btn-sm">
              View employer →
            </Link>
          </div>
        ) : (
          <EmptyState
            title="No employer attached"
            description="Match an employer to this case from the employer lookup."
            action={
              <Link to="/employers" className="btn btn-primary btn-sm">
                Find employer
              </Link>
            }
          />
        )}
      </Card>

      <Card title="Recommended effective date">
        {ed.isLoading ? (
          <LoadingIndicator text="Loading recommendation…" />
        ) : !ed.available || !ed.effectiveDate ? (
          <EmptyState
            title="No recommendation yet"
            description="The effective-date rules engine hasn't produced a recommendation for this case."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={effDateRow}>
              <div>
                <div style={effDateLabel}>Recommended</div>
                <div style={effDateValue}>{fmtDate(ed.effectiveDate.recommended_date)}</div>
              </div>
              {ed.effectiveDate.override_date && (
                <div>
                  <div style={effDateLabel}>Override</div>
                  <div style={{ ...effDateValue, color: colors.warning }}>
                    {fmtDate(ed.effectiveDate.override_date)}
                  </div>
                </div>
              )}
              {ed.effectiveDate.rule_version && (
                <StatusBadge status="neutral" label={`Rule ${ed.effectiveDate.rule_version}`} />
              )}
            </div>

            {ed.effectiveDate.override_reason && (
              <div style={mutedNote}>Override reason: {ed.effectiveDate.override_reason}</div>
            )}

            {ed.effectiveDate.factors.length > 0 && (
              <div>
                <div style={sectionTitle}>Factors</div>
                <ul style={factorList}>
                  {ed.effectiveDate.factors.map((f, i) => (
                    <li key={i} style={factorItem}>
                      <span style={{ color: colors.textSecondary }}>{f.label}:</span> {f.value}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {showOverride ? (
              <form onSubmit={handleOverride} style={form}>
                <div style={fieldRow}>
                  <div style={field}>
                    <label style={labelStyle} htmlFor="ed-date">
                      Override date
                    </label>
                    <input
                      id="ed-date"
                      type="date"
                      value={overrideDate}
                      onChange={(e) => setOverrideDate(e.target.value)}
                      className="input"
                    />
                  </div>
                  <div style={field}>
                    <label style={labelStyle} htmlFor="ed-reason">
                      Reason
                    </label>
                    <input
                      id="ed-reason"
                      type="text"
                      value={overrideReason}
                      onChange={(e) => setOverrideReason(e.target.value)}
                      className="input"
                      placeholder="Why override the recommendation?"
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={ed.isWorking || !overrideDate || !overrideReason.trim()}
                  >
                    Apply override
                  </button>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowOverride(false)}>
                    Cancel
                  </button>
                </div>
                {ed.actionError && (
                  <p style={errorText} role="alert">
                    {ed.actionError.message}
                  </p>
                )}
              </form>
            ) : (
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShowOverride(true)}>
                Override effective date
              </button>
            )}
          </div>
        )}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Stage: Application (state routing · plan eligibility · App Builder packet)
// ---------------------------------------------------------------------------

function fillTier(status: string): 'success' | 'warning' | 'neutral' {
  const s = status.toLowerCase();
  if (s === 'complete') return 'success';
  if (s === 'partial') return 'warning';
  return 'neutral';
}

function ApplicationStage({ caseId }: { caseId: string }) {
  const routing = useStateRouting(caseId);
  const plan = usePlanEligibility(caseId);
  const packet = usePacket(caseId);
  const [receipt, setReceipt] = useState<string | null>(null);

  const handleSubmit = async () => {
    const res = await packet.submit();
    if (res) setReceipt(res.receipt_id);
  };

  return (
    <>
      {/* State routing decision (ENR-031) */}
      <Card title="State routing decision">
        {routing.isLoading ? (
          <LoadingIndicator text="Loading routing…" />
        ) : !routing.available || !routing.routing ? (
          <EmptyState
            title="No routing decision yet"
            description="The rules engine hasn't produced a state-routing decision for this case."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={effDateRow}>
              <div>
                <div style={effDateLabel}>Routed to</div>
                <div style={effDateValue}>{routing.routing.decision ?? '—'}</div>
              </div>
              {routing.routing.rule_version && (
                <StatusBadge status="neutral" label={`Rule ${routing.routing.rule_version}`} />
              )}
            </div>
            {routing.routing.inputs.length > 0 && (
              <div>
                <div style={sectionTitle}>Inputs</div>
                <ul style={factorList}>
                  {routing.routing.inputs.map((inp, i) => (
                    <li key={i} style={factorItem}>
                      <span style={{ color: colors.textSecondary }}>{inp.label}:</span> {inp.value}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Plan eligibility — AI decision support (ENR-032, HITL) */}
      {plan.isLoading ? (
        <Card title="Plan eligibility">
          <LoadingIndicator text="Loading decision support…" />
        </Card>
      ) : !plan.available || !plan.eligibility ? (
        <Card title="Plan eligibility">
          <EmptyState
            title="No decision support yet"
            description="AI plan-eligibility analysis isn't connected for this case yet."
          />
        </Card>
      ) : (
        <AIInsightCard
          confidence={plan.eligibility.confidence ?? undefined}
          heading="Plan eligibility — decision support"
          acceptLabel="Accept recommendation"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div>{plan.eligibility.recommendation ?? 'No recommendation produced.'}</div>
            {plan.eligibility.evidence.length > 0 && (
              <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {plan.eligibility.evidence.map((ev, i) => (
                  <li key={i} style={{ fontSize: '0.84rem' }}>
                    {ev}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </AIInsightCard>
      )}

      {/* App Builder packet (ENR-035/036) */}
      <Card
        title="State application packet"
        actions={
          packet.available && packet.packet ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={packet.isWorking || !packet.packet.ready_to_submit || !!packet.packet.submitted_at}
              onClick={handleSubmit}
            >
              {packet.packet.submitted_at ? 'Submitted' : packet.isWorking ? 'Submitting…' : 'Submit to state'}
            </button>
          ) : undefined
        }
      >
        {packet.isLoading ? (
          <LoadingIndicator text="Loading packet…" />
        ) : !packet.available || !packet.packet ? (
          <EmptyState
            title="Packet not generated yet"
            description="The App Builder packet (forms + required attachments) isn't generated for this case yet."
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={sectionTitle}>Forms (in sequence)</div>
              {packet.packet.forms.length === 0 ? (
                <div style={mutedNote}>No forms in the packet.</div>
              ) : (
                <ul style={memberList}>
                  {[...packet.packet.forms]
                    .sort((a, b) => a.sequence - b.sequence)
                    .map((f) => (
                      <li key={f.id} style={outreachRow}>
                        <span style={cadenceStepNum}>{f.sequence}</span>
                        <span style={{ flex: 1, fontWeight: 600 }}>{f.name}</span>
                        <StatusBadge status={fillTier(f.fill_status)} label={f.fill_status.replace(/_/g, ' ')} />
                      </li>
                    ))}
                </ul>
              )}
            </div>
            <div>
              <div style={sectionTitle}>Required attachments</div>
              {packet.packet.attachments.length === 0 ? (
                <div style={mutedNote}>No attachments required.</div>
              ) : (
                <ul style={memberList}>
                  {packet.packet.attachments.map((a) => (
                    <li key={a.id} style={outreachRow}>
                      <span style={{ flex: 1 }}>{a.label}</span>
                      <StatusBadge
                        status={a.attached ? 'success' : 'warning'}
                        label={a.attached ? 'Attached' : 'Missing'}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <StatusBadge
                status={packet.packet.ready_to_submit ? 'success' : 'warning'}
                label={packet.packet.ready_to_submit ? 'Ready to submit' : 'Not ready'}
              />
              {(receipt || packet.packet.receipt_id) && (
                <span style={mutedNote}>
                  Receipt {receipt ?? packet.packet.receipt_id} · {fmtDateTime(packet.packet.submitted_at)}
                </span>
              )}
            </div>
            {packet.actionError && (
              <p style={errorText} role="alert">
                {packet.actionError.message}
              </p>
            )}
          </div>
        )}
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Stage: Enrolled (enrollment confirmation · patient-guide dispatch)
// ---------------------------------------------------------------------------

function EnrolledStage({ caseId }: { caseId: string }) {
  const enrollment = useEnrollment(caseId);

  return (
    <Card title="Enrollment">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={mutedNote}>
            Confirm enrollment with the state/plan, then dispatch the patient guide so the member knows
            their next steps.
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={enrollment.isConfirming || !!enrollment.confirmation}
              onClick={() => enrollment.confirmEnrollment()}
            >
              {enrollment.confirmation
                ? 'Enrollment confirmed'
                : enrollment.isConfirming
                  ? 'Confirming…'
                  : 'Confirm enrollment'}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={enrollment.isDispatching || !!enrollment.dispatch}
              onClick={() => enrollment.dispatchGuide()}
            >
              {enrollment.dispatch
                ? 'Guide dispatched'
                : enrollment.isDispatching
                  ? 'Dispatching…'
                  : 'Dispatch patient guide'}
            </button>
          </div>
        </div>

        {enrollment.confirmation && (
          <div style={eligResult}>
            <StatusBadge status="success" label="Enrollment confirmed" />
            <span style={mutedNote}>
              {fmtDateTime(enrollment.confirmation.confirmed_at)}
              {enrollment.confirmation.effective_date
                ? ` · effective ${fmtDate(enrollment.confirmation.effective_date)}`
                : ''}
            </span>
          </div>
        )}
        {enrollment.dispatch && (
          <div style={eligResult}>
            <StatusBadge status="info" label="Patient guide dispatched" />
            <span style={mutedNote}>
              {fmtDateTime(enrollment.dispatch.dispatched_at)}
              {enrollment.dispatch.channel ? ` · ${enrollment.dispatch.channel}` : ''}
            </span>
          </div>
        )}

        {enrollment.confirmError && (
          <p style={errorText} role="alert">
            {enrollment.confirmError.message}
          </p>
        )}
        {enrollment.dispatchError && (
          <p style={errorText} role="alert">
            {enrollment.dispatchError.message}
          </p>
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section: Clinical (referral package · decision support · go/no-go) CLN-028/029/030
// ---------------------------------------------------------------------------

const CLINICAL_DECISIONS = [
  { value: 'go', label: 'Go' },
  { value: 'no_go', label: 'No go' },
  { value: 'needs_more_info', label: 'Needs more info' },
];

function ClinicalSection({ caseId }: { caseId: string }) {
  const clinical = useClinical(caseId);
  const [decision, setDecision] = useState(CLINICAL_DECISIONS[0].value);
  const [note, setNote] = useState('');

  const handleDecision = async (e: React.FormEvent) => {
    e.preventDefault();
    const body: ClinicalDecisionBody = { decision, note: note.trim() || undefined };
    const ok = await clinical.submitDecision(body);
    if (ok) setNote('');
  };

  if (clinical.isLoading) {
    return (
      <Card title="Clinical review">
        <LoadingIndicator text="Loading clinical review…" />
      </Card>
    );
  }

  if (!clinical.available || !clinical.review) {
    return (
      <Card title="Clinical review">
        <EmptyState
          icon="🩺"
          title="Clinical review not available"
          description="The clinical referral package and decision support aren't connected for this case yet."
        />
      </Card>
    );
  }

  const r = clinical.review;

  return (
    <>
      <Card
        title="Clinical referral package"
        actions={
          r.complexity_flag ? <StatusBadge status="danger" label="Complex case" /> : undefined
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {r.complexity_flag && r.complexity_reason && (
            <div style={mutedNote}>Complexity: {r.complexity_reason}</div>
          )}

          <div>
            <div style={sectionTitle}>Provider questionnaire</div>
            {r.questionnaire.length === 0 ? (
              <div style={mutedNote}>No questionnaire responses recorded.</div>
            ) : (
              <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                {r.questionnaire.map((qa, i) => (
                  <div key={i}>
                    <dt style={{ ...overviewLabel, textTransform: 'none' }}>{qa.question}</dt>
                    <dd style={{ margin: 0, fontSize: '0.88rem' }}>{qa.answer ?? '—'}</dd>
                  </div>
                ))}
              </dl>
            )}
          </div>

          {r.clinical_summary && (
            <div>
              <div style={sectionTitle}>Clinical summary</div>
              <div style={{ fontSize: '0.88rem', lineHeight: 1.5 }}>{r.clinical_summary}</div>
            </div>
          )}

          <div>
            <div style={sectionTitle}>Attachments</div>
            {r.attachments.length === 0 ? (
              <div style={mutedNote}>No attachments.</div>
            ) : (
              <ul style={memberList}>
                {r.attachments.map((a) => (
                  <li key={a.id} style={outreachRow}>
                    <span style={{ flex: 1 }}>{a.label}</span>
                    <StatusBadge
                      status={a.attached ? 'success' : 'warning'}
                      label={a.attached ? 'Attached' : 'Missing'}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Card>

      {/* Decision-support signals (CLN-029, HITL) */}
      {r.signals.length === 0 ? (
        <Card title="Decision-support signals">
          <EmptyState title="No signals" description="No AI decision-support signals for this case." />
        </Card>
      ) : (
        r.signals.map((sig, i) => (
          <AIInsightCard key={i} confidence={sig.confidence} heading={`${sig.category} — decision support`}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div>{sig.detail}</div>
              {sig.evidence.length > 0 && (
                <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {sig.evidence.map((ev, j) => (
                    <li key={j} style={{ fontSize: '0.84rem' }}>
                      {ev}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </AIInsightCard>
        ))
      )}

      {/* Go / no-go decision (CLN-030) */}
      <Card title="Go / no-go decision">
        {r.decision && (
          <div style={{ ...eligResult, marginBottom: 12 }}>
            <StatusBadge
              status={r.decision === 'go' ? 'success' : r.decision === 'no_go' ? 'danger' : 'warning'}
              label={r.decision.replace(/_/g, ' ')}
            />
            <span style={mutedNote}>
              {fmtDateTime(r.decided_at)}
              {r.decision_note ? ` · ${r.decision_note}` : ''}
            </span>
          </div>
        )}
        <form onSubmit={handleDecision} style={form}>
          <div style={fieldRow}>
            <div style={field}>
              <label style={labelStyle} htmlFor="cln-decision">
                Decision
              </label>
              <select
                id="cln-decision"
                value={decision}
                onChange={(e) => setDecision(e.target.value)}
                style={input}
              >
                {CLINICAL_DECISIONS.map((d) => (
                  <option key={d.value} value={d.value}>
                    {d.label}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ ...field, flex: 2 }}>
              <label style={labelStyle} htmlFor="cln-note">
                Note
              </label>
              <input
                id="cln-note"
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="input"
                placeholder="Rationale (optional)"
              />
            </div>
          </div>
          <button type="submit" className="btn btn-primary" disabled={clinical.isWorking}>
            Record decision
          </button>
          {clinical.actionError && (
            <p style={errorText} role="alert">
              {clinical.actionError.message}
            </p>
          )}
        </form>
      </Card>
    </>
  );
}

// ---------------------------------------------------------------------------
// Light stages (Retention)
// ---------------------------------------------------------------------------

function LightStage({ title, note }: { title: string; note: string }) {
  return (
    <Card title={title}>
      <EmptyState title={title} description={note} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Locked stage gate
// ---------------------------------------------------------------------------

function LockedStage({ label }: { label: string }) {
  return (
    <Card title={label}>
      <EmptyState
        icon="🔒"
        title="Complete prior steps first"
        description={`The ${label} stage unlocks once the earlier stages on this case are complete.`}
      />
    </Card>
  );
}

export function CaseDetailPage() {
  const { caseId } = useParams<{ caseId: string }>();
  const {
    caseRecord,
    timeline,
    isLoading,
    isWorking,
    error,
    actionError,
    logAttempt,
    captureConsent,
  } = useCase(caseId);

  const currentIdx = currentStageIndex(caseRecord?.stage);
  const stages = useMemo(() => buildStages(currentIdx), [currentIdx]);
  // The stage the staffer is viewing (defaults to the case's current stage).
  // The special key `clinical` selects the cross-cutting Clinical section
  // rendered alongside the workflow stages (CLN-028/029/030).
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  useEffect(() => {
    setSelectedKey(LIFECYCLE[currentIdx]?.key ?? LIFECYCLE[0].key);
  }, [currentIdx]);
  const clinicalSelected = selectedKey === 'clinical';

  const selectedIdx = LIFECYCLE.findIndex((s) => s.key === selectedKey);
  const selStage = LIFECYCLE[selectedIdx] ?? LIFECYCLE[0];
  const isLocked = !clinicalSelected && selectedIdx > currentIdx + 1;

  // Consent capture form state (always available per PAC-023).
  const [consentType, setConsentType] = useState(CONSENT_TYPES[0].value);
  const [consentChannel, setConsentChannel] = useState(CONSENT_CHANNELS[0].value);
  const [consentVersion, setConsentVersion] = useState(DEFAULT_CONSENT_VERSION);

  useShellSlot({
    breadcrumb: `Case ${caseId ?? ''}`,
    stage: caseRecord ? <span style={stagePill}>{caseRecord.stage.replace(/_/g, ' ')}</span> : undefined,
  });

  const breachRisk = useMemo(() => {
    const d = caseRecord?.recommended_effective_date;
    if (!d) return false;
    return new Date(d).getTime() < Date.now();
  }, [caseRecord?.recommended_effective_date]);

  const handleLogAttempt = async (method: string, outcome: string, note: string): Promise<boolean> => {
    const created = await logAttempt({
      contact_method: method,
      outcome_code: outcome,
      notes_structured: note ? { note } : {},
    });
    return !!created;
  };

  const handleCaptureConsent = async (e: React.FormEvent) => {
    e.preventDefault();
    await captureConsent({
      consent_type: consentType,
      channel: consentChannel,
      version: consentVersion.trim() || DEFAULT_CONSENT_VERSION,
    });
  };

  const renderStage = () => {
    if (!caseRecord || !caseId) return null;
    if (clinicalSelected) return <ClinicalSection caseId={caseId} />;
    if (isLocked) return <LockedStage label={selStage.label} />;
    switch (selStage.key) {
      case 'intake':
        return <IntakeStage caseRecord={caseRecord} isWorking={isWorking} onLogAttempt={handleLogAttempt} />;
      case 'screening':
        return <ScreeningStage caseId={caseId} />;
      case 'employer_research':
        return <EmployerStage caseId={caseId} caseRecord={caseRecord} />;
      case 'application':
        return <ApplicationStage caseId={caseId} />;
      case 'enrolled':
        return <EnrolledStage caseId={caseId} />;
      case 'retention':
        return (
          <LightStage
            title="Retention"
            note="Continuous-coverage monitoring and renewals are handled in the retention workflow (later phase)."
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div style={topRow}>
        <div>
          <Link to="/cases" style={backLink}>
            ← Cases
          </Link>
          <h1 style={h1}>Case {caseId}</h1>
        </div>
        {caseRecord && (
          <div style={badgeRow}>
            <PriorityPill tier={tierForScore(caseRecord.priority_score)} />
            <StatusBadge value={caseRecord.status} />
          </div>
        )}
      </div>

      {isLoading && <LoadingIndicator text="Loading case…" />}
      {error && (
        <p style={errorText} role="alert">
          {error.message}
        </p>
      )}

      {caseRecord && (
        <>
          <Card style={{ marginBottom: 20 }}>
            <WorkflowTracker
              stages={stages}
              onStageClick={(s) => setSelectedKey(s.key)}
            />
            <div style={sectionPillRow}>
              <button
                type="button"
                onClick={() => setSelectedKey('clinical')}
                aria-pressed={clinicalSelected}
                style={{ ...clinicalPill, ...(clinicalSelected ? clinicalPillActive : {}) }}
              >
                🩺 Clinical
              </button>
              <span style={trackerHint}>
                Viewing{' '}
                <strong style={{ textTransform: 'capitalize' }}>
                  {clinicalSelected ? 'Clinical' : selStage.label}
                </strong>
                . Select a stage above (or the Clinical section) to switch the working surface; locked
                stages unlock as prior steps complete.
              </span>
            </div>
          </Card>

          <ThreePanel
            primary={
              <>
                {renderStage()}

                <Card title="Capture consent">
                  <form onSubmit={handleCaptureConsent} style={form}>
                    <div style={fieldRow}>
                      <div style={field}>
                        <label style={labelStyle} htmlFor="consent-type">
                          Consent type
                        </label>
                        <select
                          id="consent-type"
                          value={consentType}
                          onChange={(e) => setConsentType(e.target.value)}
                          style={input}
                        >
                          {CONSENT_TYPES.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={field}>
                        <label style={labelStyle} htmlFor="consent-channel">
                          Channel
                        </label>
                        <select
                          id="consent-channel"
                          value={consentChannel}
                          onChange={(e) => setConsentChannel(e.target.value)}
                          style={input}
                        >
                          {CONSENT_CHANNELS.map((c) => (
                            <option key={c.value} value={c.value}>
                              {c.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div style={field}>
                        <label style={labelStyle} htmlFor="consent-version">
                          Version
                        </label>
                        <input
                          id="consent-version"
                          type="text"
                          value={consentVersion}
                          onChange={(e) => setConsentVersion(e.target.value)}
                          className="input"
                        />
                      </div>
                    </div>
                    <button type="submit" className="btn btn-primary" disabled={isWorking}>
                      Capture consent
                    </button>
                  </form>
                </Card>

                {actionError && (
                  <p style={errorText} role="alert">
                    {actionError.message}
                  </p>
                )}
              </>
            }
            rightContext={
              <>
                <PatientSummary caseRecord={caseRecord} />
                {caseId && <EligibilityIndicator caseId={caseId} />}
                <RelatedFlags caseRecord={caseRecord} breachRisk={breachRisk} />
                {caseId && <PropensityCard caseId={caseId} />}
                {caseId && <NextBestActionCard caseId={caseId} />}
                <div>
                  <div style={sectionTitle}>Case history</div>
                  {isWorking && <LoadingIndicator text="Saving…" />}
                  <CaseHistory events={timeline} />
                </div>
              </>
            }
          />
        </>
      )}
    </>
  );
}

const topRow: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 16,
  marginBottom: 20,
};
const backLink: CSSProperties = { fontSize: '0.86rem', color: colors.action };
const h1: CSSProperties = { margin: '6px 0 0', fontSize: '1.5rem', fontWeight: 700 };
const badgeRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };
const stagePill: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '3px 12px',
  borderRadius: 999,
  background: colors.brandTint,
  color: colors.brandActive,
  fontSize: '0.78rem',
  fontWeight: 600,
  textTransform: 'capitalize',
};
const trackerHint: CSSProperties = {
  fontSize: '0.8rem',
  color: colors.textSecondary,
  lineHeight: 1.5,
};
const sectionPillRow: CSSProperties = {
  marginTop: 14,
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  flexWrap: 'wrap',
};
const clinicalPill: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '5px 14px',
  borderRadius: 999,
  border: `1px solid ${colors.border}`,
  background: colors.white,
  color: colors.textSecondary,
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
};
const clinicalPillActive: CSSProperties = {
  background: colors.action,
  borderColor: colors.action,
  color: '#FFFFFF',
};
const eligResult: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '10px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  background: colors.surface,
};
const outreachRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  fontSize: '0.86rem',
  flexWrap: 'wrap',
};
const cadenceList: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
};
const cadenceItem: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  fontSize: '0.86rem',
};
const cadenceStepNum: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 22,
  height: 22,
  borderRadius: '50%',
  background: colors.surfaceAlt,
  color: colors.textSecondary,
  fontSize: '0.75rem',
  fontWeight: 700,
  flexShrink: 0,
};
const form: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 14 };
const fieldRow: CSSProperties = { display: 'flex', gap: 12, flexWrap: 'wrap' };
const field: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minWidth: 150 };
const labelStyle: CSSProperties = {
  fontSize: '0.8rem',
  fontWeight: 600,
  color: colors.textPrimary,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
};
const input: CSSProperties = {
  padding: '8px 10px',
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  fontSize: '0.9rem',
  background: '#FFFFFF',
  fontFamily: 'inherit',
  color: colors.textPrimary,
};
const changedInput: CSSProperties = {
  border: `1px solid ${colors.warning}`,
  background: tints.warning,
};
const errorText: CSSProperties = { color: colors.danger, fontSize: '0.9rem' };
const sectionTitle: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  color: colors.textSecondary,
  marginBottom: 8,
};
const mutedNote: CSSProperties = { fontSize: '0.82rem', color: colors.textSecondary, lineHeight: 1.5 };
const summaryGrid: CSSProperties = { margin: 0, display: 'flex', flexDirection: 'column' };
const summaryRow: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: 12,
  padding: '6px 0',
  borderBottom: `1px solid ${colors.surfaceAlt}`,
};
const summaryLabel: CSSProperties = { fontSize: '0.82rem', color: colors.textSecondary };
const summaryValue: CSSProperties = { margin: 0, fontSize: '0.86rem', fontWeight: 500, textAlign: 'right' };
const overviewGrid: CSSProperties = {
  margin: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
  gap: 12,
};
const overviewItem: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 2 };
const overviewLabel: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: colors.textSecondary,
};
const overviewValue: CSSProperties = { margin: 0, fontSize: '0.95rem', fontWeight: 600, color: colors.textPrimary, textTransform: 'capitalize' };
const screeningRow: CSSProperties = { display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' };
const sourceTag: CSSProperties = {
  fontSize: '0.68rem',
  fontWeight: 600,
  padding: '1px 7px',
  borderRadius: 999,
  background: colors.surfaceAlt,
  color: colors.textSecondary,
  textTransform: 'capitalize',
};
const changedTag: CSSProperties = {
  fontSize: '0.68rem',
  fontWeight: 700,
  padding: '1px 7px',
  borderRadius: 999,
  background: tints.warning,
  color: tints.warningText,
};
const autoFillBanner: CSSProperties = {
  display: 'flex',
  gap: 10,
  alignItems: 'flex-start',
  padding: '10px 12px',
  borderRadius: 8,
  background: tints.action,
  color: tints.actionText,
  fontSize: '0.84rem',
  lineHeight: 1.45,
};
const autoFillSpark: CSSProperties = { fontSize: '0.95rem', flexShrink: 0, marginTop: 1 };
const autoFillSources: CSSProperties = { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 };
const automatedBadge: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  fontSize: '0.68rem',
  fontWeight: 700,
  padding: '1px 7px',
  borderRadius: 999,
  background: tints.action,
  color: tints.actionText,
};
const confirmCheck: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: '0.82rem',
  color: colors.textSecondary,
  paddingBottom: 9,
  whiteSpace: 'nowrap',
};
const memberList: CSSProperties = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 };
const memberItem: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  padding: '10px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  flexWrap: 'wrap',
};
const memberName: CSSProperties = { fontSize: '0.9rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
const relTag: CSSProperties = {
  fontSize: '0.7rem',
  fontWeight: 600,
  padding: '1px 8px',
  borderRadius: 999,
  background: colors.brandTint,
  color: colors.brandActive,
  textTransform: 'capitalize',
};
const memberMeta: CSSProperties = { fontSize: '0.78rem', color: colors.textSecondary };
const effDateRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' };
const effDateLabel: CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  color: colors.textSecondary,
};
const effDateValue: CSSProperties = { fontSize: '1.2rem', fontWeight: 700, color: colors.textPrimary };
const factorList: CSSProperties = { margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 };
const factorItem: CSSProperties = { fontSize: '0.84rem', color: colors.textPrimary };
const timeline: CSSProperties = { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 14 };
const timelineItem: CSSProperties = { display: 'flex', gap: 10 };
const timelineDot: CSSProperties = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: colors.action,
  marginTop: 5,
  flexShrink: 0,
};
const timelineHead: CSSProperties = { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 };
const eventTypeStyle: CSSProperties = { fontWeight: 600, fontSize: '0.86rem', textTransform: 'capitalize', color: colors.textPrimary };
const eventTime: CSSProperties = { fontSize: '0.74rem', color: colors.textSecondary, whiteSpace: 'nowrap' };
const payloadWrap: CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 };
const chip: CSSProperties = {
  fontSize: '0.74rem',
  padding: '2px 8px',
  borderRadius: 6,
  background: colors.white,
  border: `1px solid ${colors.border}`,
  color: colors.textSecondary,
};
const eventActor: CSSProperties = { fontSize: '0.74rem', color: colors.textSecondary, marginTop: 4 };
const propensityScore: CSSProperties = { fontSize: '2rem', fontWeight: 800, lineHeight: 1, color: colors.textPrimary };
const modelVsRules: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontSize: '0.8rem',
  color: colors.textSecondary,
  fontWeight: 600,
};
const factorBarRow: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8 };
const factorBarLabel: CSSProperties = { fontSize: '0.78rem', color: colors.textPrimary, flex: '0 0 110px' };
const factorBarTrack: CSSProperties = {
  flex: 1,
  height: 8,
  borderRadius: 999,
  background: colors.surfaceAlt,
  overflow: 'hidden',
};
const factorBarFill: CSSProperties = { display: 'block', height: '100%', background: colors.action, borderRadius: 999 };
const eligCardRt: CSSProperties = {
  padding: '10px 12px',
  border: `1px solid ${colors.border}`,
  borderRadius: 8,
  background: colors.white,
};
